import { SKY_LATER_WORLD_SCENERY } from './219-sky-later-world-scenery.mjs';
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js';
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js';
import { createSkyAssetCache } from './133-sky-asset-cache.mjs';
import { createSkyTextureBudget } from './173-sky-texture-budget.mjs';
import { SKY_WORLD } from '../../../packages/domain/src/86-sky-world-scale.mjs';

const WORLDS = Object.freeze({
  ...SKY_LATER_WORLD_SCENERY,
  'cathedral-of-thunder': {
    style: Object.freeze({
      background: 'worlds/03-cathedral-of-thunder',
      fog: '#3a4659',
      base: '#1c2d38',
      accent: '#e4b567',
      surface: 'flood',
    }),
    file: '03-watchtower',
    count: 10,
    layout: 'rooftops',
    gap: 68,
    clip: null,
  },
  'prism-widow': {
    style: Object.freeze({
      background: 'worlds/04-prism-widow',
      fog: '#172a48',
      base: '#101b30',
      accent: '#9deffa',
      surface: 'prism',
      terrain: false,
    }),
    file: '04-orbital',
    count: 8,
    layout: 'orbital',
    gap: 82,
    clip: 'MirrorTurn',
  },
  cinderheart: {
    style: Object.freeze({
      background: 'worlds/05-cinderheart',
      fog: '#462b31',
      base: '#201b23',
      accent: '#ffad64',
      surface: 'basalt',
    }),
    file: '05-coolant',
    count: 8,
    layout: 'ridges',
    gap: 84,
    clip: 'Cooling',
  },
  'mourning-bride': {
    style: Object.freeze({
      background: 'worlds/06-mourning-bride',
      fog: '#343951',
      base: '#202533',
      accent: '#b5c7fa',
      surface: 'moonlit',
      terrain: false,
    }),
    file: '06-wreck',
    count: 6,
    layout: 'graveyard',
    gap: 180,
    clip: 'SearchlightSweep',
  },
  'iron-orchid': {
    style: Object.freeze({
      background: 'worlds/07-iron-orchid',
      fog: '#224a46',
      base: '#263d31',
      accent: '#a3eac1',
      surface: 'garden',
    }),
    file: '07-platform',
    count: 6,
    layout: 'gardens',
    gap: 220,
    clip: 'Irrigate',
  },
});
const ids = Object.keys(WORLDS);
const cancelled = () => new DOMException('Imported world preparation cancelled', 'AbortError');
const worldFor = (stage) => (Object.hasOwn(WORLDS, stage?.worldId) ? WORLDS[stage.worldId] : null);

/** Presentation-only palette for prepared worlds03–07. Null means preserve the
 * accepted world renderer, including original dragon/whale artwork. Styles do
 * not claim that these worlds' boss signatures or environment contacts exist. */
export function skyImportedWorldStyle(stage) {
  return worldFor(stage)?.style ?? null;
}

/** The complete animated envelope defines placement, in canonical metres.
 * Exterior kits are decorative: no panel reflection, coolant contact, searchlight
 * damage or irrigation benefit is implied by playing their existing animation. */
function place(T, box, spec, index, travel, pose) {
  const row = Math.floor(index / 2),
    side = index % 2 ? 1 : -1;
  pose.position.set(0, 0, 0);
  pose.rotation.set(
    0,
    spec.layout === 'orbital'
      ? side * (0.3 + row * 0.24)
      : spec.layout === 'graveyard'
        ? side * (0.42 + row * 0.35)
        : side * (row % 2 ? 0.25 : -0.12),
    0,
  );
  if (spec.layout === 'graveyard') pose.rotation.z = side * (0.06 + row * 0.035);
  if (Number.isFinite(spec.bank)) pose.rotation.z += side * spec.bank;
  pose.updateMatrix();
  const rotated = box.clone().applyMatrix4(pose.matrix);
  const extent = side > 0 ? -rotated.min.x : rotated.max.x;
  const clearance = spec.clearance ?? SKY_WORLD.lateralLimit + 3;
  pose.position.x = side * (clearance + extent + (row % 3) * (spec.spacing ?? 7));
  const height =
    spec.layout === 'orbital' ? 10 + (row % 3) * 9 : spec.layout === 'ridges' ? (row % 3) * 1.8 : 0;
  pose.position.y = height + (spec.elevation ?? 0) - rotated.min.y;
  const cycle = Math.ceil(spec.count / 2) * spec.gap + 220;
  // Stagger opposite sides so original silhouettes approach independently,
  // rather than reading as identical gates across every imported landscape.
  const stagger = index % 2 ? spec.gap * 0.43 : 0;
  pose.position.z =
    90 -
    (spec.setback ?? 0) -
    ((((row * spec.gap + stagger + 155 - travel) % cycle) + cycle) % cycle);
  pose.updateMatrix();
}

function playback(T, scene, clip) {
  if (!clip) return null;
  const mixer = new T.AnimationMixer(scene),
    action = mixer.clipAction(clip);
  action.setLoop(T.LoopOnce, 1);
  action.clampWhenFinished = true;
  return { mixer, action, duration: clip.duration };
}

function poseAt(player, seconds) {
  if (!player) return;
  // Reset loop bookkeeping as well as mixer time: backward seeks, pause and
  // reduced-motion changes must produce the same pose as a fresh renderer.
  player.action.reset().play();
  player.mixer.setTime(seconds);
}

function releasePlayer(player, scene) {
  if (!player) return;
  player.mixer.stopAllAction();
  player.mixer.uncacheRoot(scene);
}

function posedBounds(T, scene) {
  scene.updateMatrixWorld(true);
  return new T.Box3().setFromObject(scene, true);
}

function animationEnvelope(T, scene, clip) {
  const box = posedBounds(T, scene),
    player = playback(T, scene, clip);
  if (clip) {
    // Source clips are baked at24fps. Include every authored frame, plus a2m
    // exterior margin for interpolation; gameplay never collides with these kits.
    const frames = Math.ceil(clip.duration * 24);
    for (let i = 0; i <= frames; i++) {
      poseAt(player, (clip.duration * i) / frames);
      box.union(posedBounds(T, scene));
    }
    poseAt(player, 0);
    releasePlayer(player, scene);
  }
  if (![...box.min.toArray(), ...box.max.toArray()].every(Number.isFinite) || box.isEmpty())
    throw new Error('Imported world has invalid animated metre bounds');
  return box.expandByScalar(2);
}

/** Per-renderer imported scenery. prepare resolves only after the selected
 * GLB/textures and visible instance batches exist, so callers can upload and
 * compile them before play. Selected + next two assets are the complete cache.
 * Errors propagate to readiness; retries require another explicit prepare.
 * No imported prop supplies collision or replaces a guardian. */
export function createSkyImportedWorld(T, valid = () => true) {
  const group = new T.Group();
  group.name = 'sky-imported-world';
  group.visible = false;
  const budget = createSkyTextureBudget({ valid });
  const entries = new Map();
  const frustum = new T.Frustum(),
    projection = new T.Matrix4(),
    cullBox = new T.Box3();
  let dead = false,
    selected = null,
    epoch = 0;

  async function loadAsset(id, signal) {
    const resources = new Set(),
      images = new Set(),
      textures = new Set();
    const instances = [];
    let released = false;
    function own(resource) {
      if (released) resource.dispose();
      else resources.add(resource);
      return resource;
    }
    function close() {
      if (released) return;
      released = true;
      for (const instance of instances) releasePlayer(instance.player, instance.scene);
      for (const resource of resources) resource.dispose();
      for (const image of images) budget.releaseImage(image);
      resources.clear();
      images.clear();
    }
    const loader = new GLTFLoader();
    loader.register((parser) => ({
      name: 'MYAUDIENCE_imported_world_images',
      loadTexture(index) {
        const source = parser.json.textures[index].source;
        if (source === undefined || parser.json.images[source].bufferView === undefined)
          return null;
        const imageLoader = new T.TextureLoader(parser.options.manager);
        return parser
          .loadTextureImage(index, source, {
            load(url, done, progress, failed) {
              budget
                .decode(
                  () =>
                    new Promise((resolve, reject) =>
                      imageLoader.load(url, resolve, progress, reject),
                    ),
                  signal,
                )
                .then(done, (error) => {
                  URL.revokeObjectURL(url);
                  failed(error);
                });
            },
          })
          .then((texture) => {
            if (!texture) throw new Error('Imported world texture could not decode');
            own(texture);
            textures.add(texture);
            const image = texture.source?.data;
            if (image) {
              if (released) budget.releaseImage(image);
              else images.add(image);
            }
            return texture;
          });
      },
    }));
    try {
      const variant = budget.maxDimension === 1024 && WORLDS[id].mobile !== false ? '-mobile' : '';
      const response = await fetch(
        `/assets/sky-rider/imported-worlds/${WORLDS[id].file}${variant}.glb`,
        {
          signal: AbortSignal.any([signal, AbortSignal.timeout(20000)]),
        },
      );
      if (!response.ok) throw new Error(`Imported world unavailable: ${id}`);
      const gltf = await loader.parseAsync(await response.arrayBuffer(), '');
      gltf.scene.updateMatrixWorld(true);
      const spec = WORLDS[id];
      gltf.scene.animations = gltf.animations;
      gltf.scene.traverse((node) => {
        if (!node.isMesh) return;
        own(node.geometry);
        if (node.isSkinnedMesh) own(node.skeleton);
        const materials = Array.isArray(node.material) ? node.material : [node.material];
        for (const material of materials) {
          own(material);
          for (const value of Object.values(material)) {
            if (!value?.isTexture) continue;
            own(value);
            textures.add(value);
            if (value.source?.data) images.add(value.source.data);
          }
        }
      });
      if (dead || !valid() || signal.aborted) throw cancelled();
      const clip = spec.clip
        ? gltf.animations.find((candidate) => candidate.name === spec.clip)
        : null;
      if (spec.clip && !clip) throw new Error(`Imported world missing authored clip: ${spec.clip}`);
      const bounds = animationEnvelope(T, gltf.scene, clip),
        center = bounds.getCenter(new T.Vector3());
      center.y = bounds.min.y + 2;
      const box = bounds.clone().translate(center.clone().negate()),
        root = new T.Group();
      root.name = `sky-imported-${id}`;
      root.userData.sourceClips = gltf.animations.map((candidate) => candidate.name);
      root.userData.preparedEnvironmentOnly = true;
      for (let i = 0; i < spec.count; i++) {
        const holder = new T.Group(),
          offset = new T.Group(),
          scene = cloneSkeleton(gltf.scene);
        holder.name = `sky-imported-${id}-${i}`;
        offset.position.copy(center).negate();
        scene.animations = gltf.animations;
        scene.traverse((node) => {
          if (!node.isMesh) return;
          if (node.isSkinnedMesh) own(node.skeleton);
          node.castShadow = false;
          node.receiveShadow = true;
          // An animated mesh's bind-pose sphere is not a valid frustum bound.
          // Whole-kit visibility is bounded by count and the authored travel loop.
          node.frustumCulled = false;
        });
        offset.add(scene);
        holder.add(offset);
        root.add(holder);
        instances.push({ holder, scene, player: playback(T, scene, clip) });
      }
      return {
        value: { root, box, instances, textures: [...textures] },
        dispose() {
          root.removeFromParent();
          close();
        },
      };
    } catch (error) {
      close();
      throw error;
    }
  }
  function ensure(id) {
    if (!entries.has(id)) entries.set(id, createSkyAssetCache({ load: loadAsset, valid }));
    const cache = entries.get(id);
    return cache.ensure([id]).then(() => cache.values[id]);
  }
  function draw(state, stage, decoration = { scenery: 1 }, reduced = false) {
    const spec = worldFor(stage),
      asset = entries.get(stage?.worldId)?.values[stage?.worldId];
    group.visible = Boolean(!dead && valid() && spec && asset && selected === stage.worldId);
    if (!group.visible) return;
    const travel = reduced || !Number.isFinite(state.terrainTravel) ? 0 : state.terrainTravel;
    const time = reduced || !Number.isFinite(state.time) ? 0 : Math.max(0, state.time);
    const fraction = Number.isFinite(decoration.scenery) ? decoration.scenery : 1;
    const count = Math.max(2, Math.min(spec.count, Math.ceil(spec.count * Math.max(0, fraction))));
    for (let i = 0; i < asset.instances.length; i++) {
      const instance = asset.instances[i];
      instance.qualityVisible = i < count;
      instance.holder.visible = instance.qualityVisible;
      if (!instance.holder.visible) continue;
      place(T, asset.box, spec, i, travel, instance.holder);
      if (state.cathedral?.pose?.artwork) {
        const side = Math.sign(instance.holder.position.x) || 1;
        instance.holder.position.x += side * 65;
        instance.holder.updateMatrix();
      }
      if (instance.player) {
        const duration = instance.player.duration;
        const cycle = duration * 2,
          phase = (time * 0.55 + i * 0.37) % cycle;
        poseAt(instance.player, phase <= duration ? phase : cycle - phase);
      }
    }
    group.updateMatrixWorld(true);
  }

  function cull(camera) {
    if (!group.visible) return 0;
    const asset = entries.get(selected)?.values[selected];
    if (!asset) return 0;
    camera.updateMatrixWorld(true);
    projection.multiplyMatrices(camera.projectionMatrix, camera.matrixWorldInverse);
    frustum.setFromProjectionMatrix(projection, camera.coordinateSystem, camera.reversedDepth);
    let visible = 0;
    for (const instance of asset.instances) {
      if (!instance.qualityVisible) continue;
      cullBox.copy(asset.box).applyMatrix4(instance.holder.matrixWorld);
      instance.holder.visible = frustum.intersectsBox(cullBox);
      if (instance.holder.visible) visible++;
    }
    return visible;
  }

  async function prepare(stage, { preloadOnly = false } = {}) {
    if (dead || !valid()) throw cancelled();
    const id = worldFor(stage) ? stage.worldId : null;
    // Future-stage preparation is not scene activation. The player's concurrent
    // next request must never evict the active world's still-loading cache.
    if (preloadOnly) {
      if (
        !id ||
        (selected && !ids.slice(ids.indexOf(selected), ids.indexOf(selected) + 3).includes(id))
      )
        return [];
      return (await ensure(id)).textures;
    }
    if (id !== selected) {
      selected = id;
      epoch++;
      group.clear();
      group.visible = false;
    }
    const current = epoch;
    const keep = id ? ids.slice(ids.indexOf(id), ids.indexOf(id) + 3) : [];
    for (const [key, cache] of entries) {
      if (keep.includes(key)) continue;
      cache.dispose();
      entries.delete(key);
    }
    if (!id) return [];
    const asset = await ensure(id);
    if (dead || !valid() || epoch !== current) throw cancelled();
    group.add(asset.root);
    draw({ terrainTravel: 0 }, stage);
    for (const next of keep.slice(1)) {
      // Background failure is surfaced if that world is explicitly selected.
      void ensure(next).catch(() => {});
    }
    return asset.textures;
  }
  function dispose() {
    if (dead) return;
    dead = true;
    epoch++;
    for (const cache of entries.values()) cache.dispose();
    entries.clear();
    budget.dispose();
    group.clear();
    group.visible = false;
    group.removeFromParent();
  }
  return { group, prepare, draw, cull, dispose };
}
